import React, { useState } from 'react';

// Check if a dance qualifies as "ambi" (both Lead AND Follow at Intermediate or Advanced)
const isAmbiDancer = (dance) => {
  const qualifyingLevels = ['intermediate', 'advanced'];
  const followLevel = dance.follow?.toLowerCase();
  const leadLevel = dance.lead?.toLowerCase();

  return qualifyingLevels.includes(followLevel) && qualifyingLevels.includes(leadLevel);
};

// Format role levels for display
const formatRoleLevels = (dance) => {
  const parts = [];
  if (dance.follow && dance.follow !== 'not-interested') {
    parts.push(`F: ${dance.follow.charAt(0).toUpperCase()}`);
  }
  if (dance.lead && dance.lead !== 'not-interested') {
    parts.push(`L: ${dance.lead.charAt(0).toUpperCase()}`);
  }
  return parts.join(' / ');
};

const StatCard = ({ stat }) => (
  <div
    className="stat-card"
    onClick={stat.action}
    style={{ '--accent-color': stat.color }}
  >
    <div className="stat-icon" style={{ background: stat.color }}>
      <i className={`bi ${stat.icon}`}></i>
    </div>
    <div className="stat-info">
      <span className="stat-value">{stat.value}</span>
      <span className="stat-label">{stat.label}</span>
    </div>
  </div>
);

const OverviewSection = ({ stats, profileData, onNavigate, navigate, onPhotoClick }) => {
  const [hoveredAmbi, setHoveredAmbi] = useState(null);

  const eventCards = [
    { label: 'Attended', value: stats.eventsAttended || 0, icon: 'bi-calendar-check', color: '#54A0FF', action: () => onNavigate('events-attended') },
    { label: 'Reviewed', value: stats.totalReviews || 0, icon: 'bi-chat-square-quote', color: '#8CB9A3', action: () => onNavigate('reviews') },
    { label: 'Liked', value: stats.favoriteEvents || 0, icon: 'bi-heart-fill', color: '#A65474', action: () => onNavigate('events-liked') },
    { label: 'Created', value: stats.createdEvents || 0, icon: 'bi-plus-circle', color: '#6B3A5B', action: () => onNavigate('events-created') },
  ];

  const articleCards = [
    { label: 'Liked', value: stats.likedArticles || 0, icon: 'bi-heart-fill', color: '#A65474', action: () => onNavigate('articles-liked') },
    { label: 'Written', value: (stats.totalArticles || 0) + (stats.totalDrafts || 0), icon: 'bi-journal-check', color: '#6B3A5B', action: () => onNavigate('articles-published') },
  ];

  const kudosCards = [
    { label: 'Given', value: stats.totalKudosGiven || 0, icon: 'bi-send-check', color: '#9B59B6', action: () => onNavigate('kudos-given') },
    { label: 'Received', value: stats.totalKudosReceived || 0, icon: 'bi-gift', color: '#A65474', action: () => onNavigate('kudos-received') },
  ];

  const goodsCards = [
    { label: 'Added', value: stats.totalGoods || 0, icon: 'bi-bag-heart', color: '#E67E22', action: () => onNavigate('goods') },
    { label: 'Saved', value: stats.savedGoods || 0, icon: 'bi-bookmark-heart', color: '#E8C547', action: () => onNavigate('goods-saved') },
  ];

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
    });
  };

  return (
    <div className="section-content overview-section">
      <div className="section-header overview-welcome">
        <div>
          <h1>Welcome back, {profileData?.fullName?.split(' ')[0] || 'Dancer'}!</h1>
          <p className="section-subtitle">Here's your activity summary</p>
        </div>
      </div>

      {/* Grouped Stats Layout */}
      <div className="stats-grouped-layout">
        <div className="stats-group stats-group--events">
          <h4 className="stats-group-title"><i className="bi bi-calendar-event"></i> Events</h4>
          <div className="stats-group-grid stats-group-grid--2x2">
            {eventCards.map((stat, idx) => (
              <StatCard key={idx} stat={stat} />
            ))}
          </div>
        </div>
        <div className="stats-group stats-group--articles">
          <h4 className="stats-group-title"><i className="bi bi-journal-text"></i> Articles</h4>
          <div className="stats-group-grid stats-group-grid--pair">
            {articleCards.map((stat, idx) => (
              <StatCard key={idx} stat={stat} />
            ))}
          </div>
        </div>
        <div className="stats-group stats-group--kudos">
          <h4 className="stats-group-title"><i className="bi bi-award"></i> Kudos</h4>
          <div className="stats-group-grid stats-group-grid--pair">
            {kudosCards.map((stat, idx) => (
              <StatCard key={idx} stat={stat} />
            ))}
          </div>
        </div>
        <div className="stats-group stats-group--goods">
          <h4 className="stats-group-title"><i className="bi bi-bag-heart"></i> Goods</h4>
          <div className="stats-group-grid stats-group-grid--pair">
            {goodsCards.map((stat, idx) => (
              <StatCard key={idx} stat={stat} />
            ))}
          </div>
        </div>
      </div>

      {/* Profile Summary */}
      <div className="overview-cards">
        <div className="overview-card profile-summary-card">
          <h3><i className="bi bi-person-badge"></i> Profile Summary</h3>
          <div className="profile-summary-content">
            <div className="summary-row">
              <span className="summary-label">Member Since</span>
              <span className="summary-value">{formatDate(profileData?.createdAt)}</span>
            </div>
            <div className="summary-row">
              <span className="summary-label">City</span>
              <span className="summary-value">{profileData?.city || 'Not set'}</span>
            </div>
            <div className="summary-row">
              <span className="summary-label">Dance Styles</span>
              <span className="summary-value">{profileData?.dances?.length || 0} styles</span>
            </div>
            {profileData?.moveMuse && (
              <div className="summary-quote">
                <i className="bi bi-quote"></i>
                <p>{profileData.moveMuse}</p>
              </div>
            )}
          </div>
        </div>

        {/* Dance Styles Card */}
        {profileData?.dances?.length > 0 && (
          <div className="overview-card dance-styles-card">
            <h3><i className="bi bi-music-note-beamed"></i> My Dance Styles</h3>
            <div className="dance-styles-list-overview">
              {profileData.dances.slice(0, 6).map((dance, idx) => {
                const isAmbi = isAmbiDancer(dance);
                return (
                  <div
                    key={idx}
                    className={`dance-style-item ${isAmbi ? 'ambi' : ''}`}
                    onMouseEnter={() => isAmbi && setHoveredAmbi(idx)}
                    onMouseLeave={() => setHoveredAmbi(null)}
                  >
                    <div className="dance-style-header">
                      <span className="dance-name">{dance.style || dance.name}</span>
                      {isAmbi && (
                        <span className="ambi-indicator" title="An ambi-dancer here!">
                          ☯
                          {hoveredAmbi === idx && (
                            <span className="ambi-tooltip">An ambi-dancer here!</span>
                          )}
                        </span>
                      )}
                    </div>
                    <div className="dance-role-levels">
                      {formatRoleLevels(dance)}
                    </div>
                  </div>
                );
              })}
              {profileData.dances.length > 6 && (
                <div className="dance-style-item more-indicator">
                  <span>+{profileData.dances.length - 6} more styles</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="quick-actions">
        <h3>Quick Actions</h3>
        <div className="action-buttons">
          <button className="action-btn" onClick={() => onNavigate('articles-drafts')}>
            <i className="bi bi-pencil-square"></i>
            <span>Write Article</span>
          </button>
          <button className="action-btn" onClick={() => navigate('/events')}>
            <i className="bi bi-calendar-plus"></i>
            <span>Find Events</span>
          </button>
          <button className="action-btn" onClick={() => onNavigate('settings')}>
            <i className="bi bi-gear"></i>
            <span>Edit Profile</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default OverviewSection;
